import { createTheme, Theme } from '@mui/material/styles';
import typographyConfig from './typography';
import { lightPalette, darkPalette } from './palette';
import defaultPropsConfig from './defaultProps';

/**
 * Base MUI theme configuration shared between light and dark modes.
 *
 * Combines:
 * - Global typography settings
 * - Global component default props and overrides
 */

// Create base theme with shared configuration
const baseTheme = createTheme({
  ...typographyConfig,
  ...defaultPropsConfig,
});

/**
 * Intermediate light theme created from the base theme and light palette.
 * Used as the foundation for the exported `lightTheme`.
 */
const lightThemeBase = createTheme(baseTheme, {
  palette: lightPalette,
});

/**
 * Light mode theme for the Elevator design system.
 * Includes custom palette fields (custom, brands, submissionStatuses, getRgba).
 */
export const lightTheme: Theme = createTheme(lightThemeBase, {
  palette: {
    custom: lightPalette.custom,
    getRgba: lightPalette.getRgba,
    brands: lightPalette.brands,
    submissionStatuses: lightPalette.submissionStatuses,
  },
});

// Create dark theme - Start with fresh dark mode base
const pureDarkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

/**
 * Intermediate dark theme created from a pure dark base, base theme config,
 * and the dark palette. Used as the foundation for the exported `darkTheme`.
 */
const darkThemeBase = createTheme(pureDarkTheme, {
  ...baseTheme,
  palette: {
    ...pureDarkTheme.palette, // Keep MUI's auto-generated dark colors
    ...darkPalette, // Override with our custom dark palette
  },
});

/**
 * Dark mode theme for the Elevator design system.
 * Extends the base theme with dark palette values and custom fields.
 */
export const darkTheme: Theme = createTheme(darkThemeBase, {
  palette: {
    ...darkThemeBase.palette,
    custom: darkPalette.custom,
    getRgba: darkPalette.getRgba,
    brands: darkPalette.brands,
    submissionStatuses: darkPalette.submissionStatuses,
  },
});

/**
 * Default theme export for backwards compatibility.
 * Alias for the light mode theme.
 */
export default lightTheme;
